import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import type { Location } from '../../api/config';

/**
 * Recipient + scheduling fields for digest emails. Side effects that depend
 * on `digest_restock_day_of_week` (e.g. the modal's auto-fill of
 * `quote_confirmation_deadline` when blank) live at the wrapper layer — this
 * component just emits patches.
 */
export type DigestRecipientValue = Pick<
  Location,
  | 'digest_recipient_email'
  | 'digest_recipient_name'
  | 'digest_restock_day_of_week'
  | 'restock_interval_weeks'
  | 'restock_anchor_date'
>;

interface DigestRecipientFieldsProps {
  value: Partial<DigestRecipientValue>;
  onChange: (patch: Partial<DigestRecipientValue>) => void;
  density?: 'standard' | 'compact';
}

interface RestockCadenceErrors {
  restock_interval_weeks?: string;
  restock_anchor_date?: string;
}

/**
 * Cadence states the backend will reject (mirrors the Location model
 * validations). Shared by the inline field errors here and LocationForm's
 * submit gate, so the two can't drift.
 */
export const restockCadenceErrors = (
  value: Partial<DigestRecipientValue>,
): RestockCadenceErrors => {
  const interval = value.restock_interval_weeks;
  // The props type is Partial, so a caller may omit the field entirely —
  // that means "backend default of 1 applies", not "invalid". Both current
  // callers seed a definite value; this keeps the contract honest anyway.
  if (interval === undefined) return {};
  if (interval === null || !Number.isInteger(interval) || interval < 1 || interval > 52) {
    return { restock_interval_weeks: 'Must be a whole number between 1 and 52' };
  }
  if (interval > 1 && !value.restock_anchor_date) {
    return { restock_anchor_date: 'Required when restocking every 2+ weeks' };
  }
  return {};
};

const DAY_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export const DigestRecipientFields: React.FC<DigestRecipientFieldsProps> = ({
  value,
  onChange,
  density = 'standard',
}) => {
  const size = density === 'compact' ? 'small' : undefined;
  const fullWidth = density === 'standard';
  const dayValue =
    value.digest_restock_day_of_week === null || value.digest_restock_day_of_week === undefined
      ? ''
      : value.digest_restock_day_of_week;
  // undefined = field omitted by the caller (Partial props) → display the
  // backend default of 1; null = user cleared the input mid-edit → keep it
  // visibly empty.
  const intervalValue =
    value.restock_interval_weeks === undefined ? 1 : value.restock_interval_weeks ?? '';
  const cadenceErrors = restockCadenceErrors(value);
  // Anchor stays visible while the interval is cleared (null) so the date
  // isn't yanked out from under a user retyping the interval.
  const showAnchor = value.restock_interval_weeks === null || (value.restock_interval_weeks ?? 1) > 1;

  return (
    <>
      <TextField
        size={size}
        name="digest_recipient_email"
        label="Digest Recipient Email"
        type="text"
        value={value.digest_recipient_email || ''}
        onChange={e => onChange({ digest_recipient_email: e.target.value })}
        fullWidth={fullWidth}
        helperText={
          density === 'standard'
            ? "Email address(es) to receive inventory digests. Multiple addresses can be separated by commas, colons, or semicolons. If not set, organization's primary contact email will be used."
            : 'Comma-separated for multiple'
        }
      />

      <TextField
        size={size}
        name="digest_recipient_name"
        label="Digest Recipient Name"
        type="text"
        value={value.digest_recipient_name || ''}
        onChange={e => onChange({ digest_recipient_name: e.target.value })}
        fullWidth={fullWidth}
        helperText={
          density === 'standard'
            ? "Name(s) used in email greetings (e.g., 'John' or 'John, Sarah'). If not set, the location name will be used."
            : undefined
        }
      />

      <FormControl size={size} fullWidth={fullWidth}>
        <InputLabel>Restock day of week — quote email send day</InputLabel>
        <Select
          name="digest_restock_day_of_week"
          value={dayValue}
          onChange={e => {
            const newDay = e.target.value === '' ? null : Number(e.target.value);
            onChange({ digest_restock_day_of_week: newDay });
          }}
          label="Restock day of week — quote email send day"
        >
          <MenuItem value="">Not Set</MenuItem>
          {DAY_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size={size}
        name="restock_interval_weeks"
        label="Restock every N weeks"
        type="number"
        value={intervalValue}
        onChange={e => {
          const raw = e.target.value;
          onChange({ restock_interval_weeks: raw === '' ? null : Number(raw) });
        }}
        fullWidth={fullWidth}
        inputProps={{ min: 1, max: 52, step: 1 }}
        error={!!cadenceErrors.restock_interval_weeks}
        helperText={cadenceErrors.restock_interval_weeks ?? '1 = every week'}
      />

      {showAnchor && (
        <TextField
          size={size}
          name="restock_anchor_date"
          label="Anchor date"
          type="date"
          value={value.restock_anchor_date || ''}
          onChange={e => onChange({ restock_anchor_date: e.target.value || null })}
          fullWidth={fullWidth}
          InputLabelProps={{ shrink: true }}
          error={!!cadenceErrors.restock_anchor_date}
          helperText={
            cadenceErrors.restock_anchor_date
              ?? (density === 'standard'
                ? 'Restock digests send on the restock day of every Nth week, counted from the week containing this date.'
                : undefined)
          }
        />
      )}
    </>
  );
};
